import { useState, useEffect } from "preact/hooks";
import type { JSX } from "preact";
import { ChatInterface } from "./ChatInterface/ChatInterface";
import { createPortal } from "preact/compat";
import {
  subscribeToMonitoringService,
  getMonitoringService,
} from "../lib/monitoring";
import { config } from "../config";
import useMetadataStore from "@/stores/metaDataStore";
import { useKismetConfig } from "../contexts/KismetContext";
import { ChatbotTrigger } from "./Trigger/ChatbotTrigger";
import { isOfferExpired } from "../lib/utils/isOfferExpired";

/**
 * createPortal allows us to render children components into a DOM node that exists
 * outside the parent component's DOM hierarchy.
 *
 * Why use a Portal for modals?
 * 1. Modals should be rendered at the root level of the document to avoid:
 *    - z-index conflicts with parent elements
 *    - CSS inheritance issues (especially with positioning)
 *    - Being clipped by parent overflow settings
 *
 * 2. Even though the modal is rendered elsewhere in the DOM, it behaves like a
 *    normal child component (receives props and context)
 *
 * We portal both the Trigger and ChatInterface because:
 * 1. Both need to be above all other page content
 * 2. Both need to be positioned fixed relative to the viewport
 * 3. This ensures consistent stacking context and interaction behavior
 */

/**
 * Types for the Chatbot component's state management
 */
interface ChatMessage {
  id: string;
  text: string;
  type: "user" | "bot";
  timestamp: string;
}

interface MessageContent {
  type: "input_text" | "output_text" | "rich_preview";
  text?: string;
  component?: JSX.Element | null;
}

interface ConversationMessage {
  type: "message";
  role: "assistant" | "user";
  content: MessageContent[];
}

interface ChatbotState {
  isOpen: boolean;
  messages: ChatMessage[];
  currentInput: string;
  lastInteraction: string;
  error?: string;
}

interface DashboardCommand {
  type: string;
  data: {
    action?: "open" | "close";
    message?: string;
    timestamp: string;
  };
}

interface ChatbotProps {
  onUrlChange?: (newUrl: string) => void;
}

interface ChatInterfaceProps {
  onClose: () => void;
  logoSrc?: string;
}

export const Chatbot = () => {
  const kismetConfig = useKismetConfig();
  const { setOfferMetadata } = useMetadataStore();
  const [monitoringService, setMonitoringService] =
    useState(getMonitoringService);

  // Subscribe to monitoring service updates
  useEffect(() => {
    const unsubscribe = subscribeToMonitoringService((instance) => {
      console.debug("Chatbot received updated monitoring service instance");
      setMonitoringService(instance);
    });

    return unsubscribe;
  }, []);

  const [chatbotState, setChatbotState] = useState<ChatbotState>(() => {
    // Initialize with closed state
    const startingChatbotState = {
      isOpen: false,
      messages: [],
      currentInput: "",
      lastInteraction: new Date().toISOString(),
    };
    monitoringService.onChatbotStateChange(startingChatbotState);
    return startingChatbotState;
  });

  // Effect to fetch offer and handle URL parameters
  useEffect(() => {
    const fetchOffer = async () => {
      try {
        const metadata = await monitoringService.fetchPersonalOffer();
        console.log("metadata", metadata);

        // Explicitly check if the offer exists and is valid
        const offerExists = metadata && Object.keys(metadata).length > 0;

        if (offerExists) {
          // Check if offer has expired
          if (isOfferExpired(metadata.expiresAt)) {
            console.debug("Offer has expired, showing generic form");
            metadata.isExpired = true;
          }

          // Store metadata in the metadata store
          setOfferMetadata(metadata);

          // Check URL parameters and open modal only if we have a valid offer
          const urlParams = new URLSearchParams(window.location.search);
          const shouldOpenFromUrl =
            urlParams.get(config.chatbot.open_modal_url_param) === "open";

          if (shouldOpenFromUrl) {
            // We have a valid offer and the URL parameter is set to open
            console.debug(
              "Opening modal based on URL parameter with valid offer"
            );
            setChatbotState((prev) => ({
              ...prev,
              isOpen: true,
              lastInteraction: new Date().toISOString(),
            }));
          }
        } else {
          console.debug("No valid offer found, not opening modal");
        }
      } catch (error) {
        console.error("Error fetching offer:", error);
      }
    };

    fetchOffer();
  }, [monitoringService]); // Run when monitoringService changes

  // Effect to emit state changes
  useEffect(() => {
    monitoringService.onChatbotStateChange(chatbotState);
  }, [chatbotState, monitoringService]);

  // Effect to handle toggle commands and URL changes
  useEffect(() => {
    // Call onUrlChange from config when available
    if (kismetConfig.onUrlChange) {
      // Use an async wrapper to satisfy the Promise<void> return type
      const originalOnUrlChange = monitoringService.onUrlChange;
      monitoringService.onUrlChange = async (newUrl: string) => {
        // Call the original method
        await originalOnUrlChange.call(monitoringService, newUrl);
        // Then call the config's onUrlChange
        kismetConfig.onUrlChange?.(newUrl);
      };
    }

    // Listen for chatbot control commands
    const cleanup = monitoringService.onIncomingSocketEvent(
      "dashboard_command",
      (command: DashboardCommand) => {
        console.log("Received command in Chatbot:", command);

        switch (command.type) {
          case "toggle_chatbot":
            switch (command.data.action) {
              case "open":
                setChatbotState((prev) => ({
                  ...prev,
                  isOpen: true,
                  lastInteraction: new Date().toISOString(),
                }));
                break;
              case "close":
                setChatbotState((prev) => ({
                  ...prev,
                  isOpen: false,
                  lastInteraction: new Date().toISOString(),
                }));
                break;
            }
            break;
        }
      }
    );

    return () => {
      cleanup();
    };
  }, [kismetConfig.onUrlChange, monitoringService]);

  const handleNewMessage = (message: string) => {
    const newMessage: ChatMessage = {
      id: crypto.randomUUID(),
      text: message,
      type: "user",
      timestamp: new Date().toISOString(),
    };

    setChatbotState((prev) => ({
      ...prev,
      messages: [...prev.messages, newMessage],
      currentInput: "",
      lastInteraction: new Date().toISOString(),
    }));
  };

  return createPortal(
    <>
      <div className="kismet-button fixed bottom-0 right-0 z-50 p-4 md:p-8 flex justify-end">
        <ChatbotTrigger
          setIsOpen={(isOpen: boolean) =>
            setChatbotState((prev) => ({
              ...prev,
              isOpen,
              lastInteraction: new Date().toISOString(),
            }))
          }
        />
      </div>
      {chatbotState.isOpen && (
        <ChatInterface
          onClose={() =>
            setChatbotState((prev) => ({
              ...prev,
              isOpen: false,
              lastInteraction: new Date().toISOString(),
            }))
          }
        />
      )}
    </>,
    document.body
  );
};
